import { useEffect, useId, useRef, type ReactNode } from 'react';

import { Button } from './Button';
import { CloseIcon } from './icons';
import styles from './Modal.module.css';

/**
 * A modal dialog — design addon §8.8.
 *
 * Built on the native `<dialog>` element rather than a div with a high
 * z-index. `showModal()` brings the focus trap, the Escape key, inertness of
 * the page behind, and the top layer with it. Hand-rolling those is how a
 * dialog ends up letting Tab wander into the page underneath, which a screen
 * reader user cannot see has happened.
 */
export interface ModalProps {
  readonly title: string;
  readonly description?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}

export function Modal({ title, description, onClose, children }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // `open` as an attribute renders it inline and non-modal; only this call
    // puts it in the top layer with the rest of the page inert.
    if (!dialog.open) dialog.showModal();

    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-labelledby={titleId}
      /* Escape fires this; without it the element would close itself while the
         state that rendered it still says it is open. */
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      /* A click on the backdrop lands on the dialog element itself, never on
         its contents — §8.8 wants a clear way out. */
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className={styles.panel}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} aria-label="إغلاق">
            <CloseIcon />
          </Button>
        </header>

        <div className={styles.body}>{children}</div>
      </div>
    </dialog>
  );
}
